/**
 * Hook for handling generic redirect actions
 */
import { useCallback } from 'react';
import type { Payment, PaymentOptions } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';

interface UseRedirectActionParams {
    setIsLoading: (loading: boolean) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
}

export function useRedirectAction({ setIsLoading, hookOptionsRef }: UseRedirectActionParams) {
    const handleRedirect = useCallback(
        async (payment: Payment, actionData: any, options: PaymentOptions = {}) => {
            // For redirect type, let funnel orchestrator handle navigation via callbacks
            // Only auto-redirect if explicitly enabled (disableAutoRedirect: false)
            const shouldAutoRedirect = options.disableAutoRedirect === false;

            if (shouldAutoRedirect && actionData.metadata?.redirect?.redirectUrl) {
                window.location.href = actionData.metadata.redirect.redirectUrl;
            } else if (payment.status === 'succeeded') {
                // Payment succeeded - call success callbacks for funnel navigation
                setIsLoading(false);
                const response = {
                    paymentId: payment.id,
                    payment,
                    order: (payment as any).order,
                };

                // Hook-level callback (universal handler)
                if (hookOptionsRef.current?.onPaymentCompleted) {
                    await hookOptionsRef.current.onPaymentCompleted(payment, {
                        isRedirectReturn: false,
                        order: response.order,
                    });
                }

                // Legacy callback (backwards compatibility)
                options.onSuccess?.(response);
                // Funnel-aligned callback (recommended)
                options.onPaymentSuccess?.(response);
            }
        },
        [setIsLoading, hookOptionsRef],
    );

    return { handleRedirect };
}
